import {
    extractTotalScore,
    getWheelCharactersFromWords,
    parsePuzzleDetails,
} from "./wordWheelDisplayUtils";

export const WORD_WHEEL_SEASON_OPTIONS = [
    "season_2026",
];

export const WORD_WHEEL_DEFAULT_SEASON = "season_2026";

/** Template / stats filter for puzzles with no journey season (daily catalog). */
export const WORD_WHEEL_SEASON_NONE = "none";

/** Published puzzles per season before levels are auto-assigned and locked. */
export const SEASON_LEVEL_CATALOG_MIN = 100;

/** Max journey / puzzle level (1 = easiest). */
export const SEASON_MAX_LEVEL = 1100;

/** Primary difficulty step: word count 3 → 4 → 5… (fewer words = easy). */
const WORD_COUNT_TIER_STEP = 280;
const WORD_LENGTH_WEIGHT = 40;
const DISTINCT_WEIGHT = 8;
const MAX_WITHIN_BAND = 270;

const SEASON_TAG_PREFIX = "season_";

export function isSeasonTag(tag) {
    return String(tag || "").toLowerCase().startsWith(SEASON_TAG_PREFIX);
}

export function isJourneyPack(season) {
    return String(season || "").trim().toLowerCase() === WORD_WHEEL_DEFAULT_SEASON;
}

export function extractSeasonFromRow(row) {
    if (row?.season) {
        return String(row.season);
    }
    const tags = Array.isArray(row?.tags) ? row.tags : [];
    const seasonTag = tags.find(isSeasonTag);
    if (seasonTag) {
        return String(seasonTag);
    }
    const details = parsePuzzleDetails(row?.details);
    return details?.season ? String(details.season) : "";
}

export function mergeSeasonIntoTags(tags, season) {
    const existing = Array.isArray(tags) ? tags.map(String) : [];
    const withoutSeason = existing.filter((tag) => !isSeasonTag(tag));
    if (season) {
        withoutSeason.push(season);
    }
    return withoutSeason;
}

export function getWordCountFromRow(row) {
    if (row?.wordsInUse) {
        return row.wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean).length;
    }
    return 0;
}

/** Longest word length in wordsInUse (0 if empty). */
export function getMaxWordLengthFromRow(row) {
    if (!row?.wordsInUse) {
        return 0;
    }
    let max = 0;
    for (const line of String(row.wordsInUse).split("\n")) {
        const word = line.trim();
        if (word) {
            max = Math.max(max, word.length);
        }
    }
    return max;
}

/**
 * Season / pack reorder / calculated difficulty sort key helpers.
 * Easier first: fewer words, shorter longest-word, fewer characters, higher total score.
 */
export function comparePuzzleDifficulty(a, b) {
    const wordsA = getWordCountFromRow(a);
    const wordsB = getWordCountFromRow(b);
    if (wordsA !== wordsB) {
        return wordsA - wordsB;
    }
    const lenA = Math.max(3, getMaxWordLengthFromRow(a) || 3);
    const lenB = Math.max(3, getMaxWordLengthFromRow(b) || 3);
    if (lenA !== lenB) {
        return lenA - lenB;
    }
    const charA = Math.max(3, getCharacterCountFromRow(a) || 3);
    const charB = Math.max(3, getCharacterCountFromRow(b) || 3);
    if (charA !== charB) {
        return charA - charB;
    }
    const scoreA = extractTotalScore(a?.details) ?? 0;
    const scoreB = extractTotalScore(b?.details) ?? 0;
    if (scoreA !== scoreB) {
        return scoreB - scoreA;
    }
    return String(a?.id || "").localeCompare(String(b?.id || ""));
}

/**
 * Lower value = easier puzzle. Matches season/pack reorder:
 * word count (primary), max word length, characters, total score high = easy.
 */
export function calculatePuzzleLevel(row) {
    let maxLen = getMaxWordLengthFromRow(row);
    if (!Number.isFinite(maxLen) || maxLen < 3) {
        maxLen = 3;
    }

    let distinctCount = getCharacterCountFromRow(row);
    if (!Number.isFinite(distinctCount) || distinctCount < 3) {
        distinctCount = 3;
    }

    const wordCount = getWordCountFromRow(row);
    const base = Math.max(0, wordCount - 3) * WORD_COUNT_TIER_STEP + 1;

    let withinBand = Math.max(0, maxLen - 3) * WORD_LENGTH_WEIGHT;
    withinBand += Math.max(0, distinctCount - 3) * DISTINCT_WEIGHT;

    const totalScore = extractTotalScore(row?.details);
    if (totalScore != null && totalScore > 0) {
        const logScore = Math.log10(totalScore);
        const clampedLog = Math.max(3, Math.min(6.5, logScore));
        withinBand += Math.round(((6.5 - clampedLog) / 3.5) * 100);
    } else {
        withinBand += 50;
    }

    withinBand = Math.min(withinBand, MAX_WITHIN_BAND);
    return Math.max(1, Math.min(SEASON_MAX_LEVEL, Math.round(base + withinBand)));
}

export function isLevelLocked(row) {
    if (row?.levelLocked === true) {
        return true;
    }
    const details = parsePuzzleDetails(row?.details);
    return details?.levelLocked === true;
}

export function getDisplayPuzzleLevel(row) {
    if (row?.puzzleLevel != null && row.puzzleLevel !== "" && Number.isFinite(Number(row.puzzleLevel))) {
        return Number(row.puzzleLevel);
    }
    const details = parsePuzzleDetails(row?.details);
    const stored = details?.puzzleLevel;
    if (stored != null && stored !== "" && Number.isFinite(Number(stored))) {
        return Number(stored);
    }
    const calculated = details?.calculatedLevel;
    if (calculated != null && calculated !== "" && Number.isFinite(Number(calculated))) {
        return Number(calculated);
    }
    return calculatePuzzleLevel(row);
}

export function buildPublishMetadata(row, wordScores, { season } = {}) {
    const existing = parsePuzzleDetails(row?.details) || {};
    const seasonId = season ? String(season).trim() : "";
    const details = {
        ...(wordScores || {}),
        ...existing,
    };
    if (seasonId) {
        details.season = seasonId;
    } else {
        details.season = "";
        details.puzzleLevel = null;
        details.levelLocked = false;
    }

    return { details };
}

/** Default publish comment from a season id, e.g. season_2026 → "Season 2026". */
export function formatSeasonComment(season) {
    if (!season) {
        return "";
    }
    const normalized = String(season).trim();
    const match = normalized.match(/^season_(\d{4})(?:_(.+))?$/i);
    if (match) {
        const suffix = match[2] ? ` ${match[2].replace(/_/g, " ")}` : "";
        return `Season ${match[1]}${suffix}`;
    }
    return normalized.replace(/_/g, " ");
}

export function distributeSeasonLevels(sortedTemplates) {
    const count = sortedTemplates.length;
    if (count === 0) {
        return [];
    }
    if (count === 1) {
        return [{ tpl: sortedTemplates[0], puzzleLevel: Math.round(SEASON_MAX_LEVEL / 2) }];
    }
    return sortedTemplates.map((tpl, index) => ({
        tpl,
        puzzleLevel: Math.max(
            1,
            Math.min(SEASON_MAX_LEVEL, Math.round(1 + (index / (count - 1)) * (SEASON_MAX_LEVEL - 1))),
        ),
    }));
}

export function hasAssignedPuzzleLevel(row) {
    if (row?.puzzleLevel != null && row.puzzleLevel !== "" && Number.isFinite(Number(row.puzzleLevel))) {
        return true;
    }
    const details = parsePuzzleDetails(row?.details);
    const stored = details?.puzzleLevel;
    return stored != null && stored !== "" && Number.isFinite(Number(stored));
}

export function summarizeSeasonPuzzles(puzzles) {
    const assigned = puzzles.filter((puzzle) => hasAssignedPuzzleLevel(puzzle));
    const levels = assigned.map((puzzle) => getDisplayPuzzleLevel(puzzle));

    return {
        total: puzzles.length,
        withLevel: assigned.length,
        withoutLevel: puzzles.length - assigned.length,
        locked: puzzles.filter((puzzle) => isLevelLocked(puzzle)).length,
        levelMin: levels.length ? Math.min(...levels) : null,
        levelMax: levels.length ? Math.max(...levels) : null,
        catalogComplete: isSeasonCatalogComplete(puzzles.length),
    };
}

export function isSeasonCatalogComplete(publishedCount) {
    return publishedCount >= SEASON_LEVEL_CATALOG_MIN;
}

export function getCharacterCountFromRow(row) {
    const details = parsePuzzleDetails(row?.details);
    const fromDetails = details?.characterCount;
    if (fromDetails != null && fromDetails !== "" && Number.isFinite(Number(fromDetails))) {
        return Number(fromDetails);
    }
    return getWheelCharactersFromWords(row?.wordsInUse).length;
}

function roundStat(value) {
    return Math.round(value * 10) / 10;
}

function averageValues(values) {
    if (!values.length) {
        return null;
    }
    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function buildSeasonPublishedGroupsByCharacters(puzzles) {
    const groups = new Map();

    puzzles.forEach((puzzle) => {
        const characterCount = getCharacterCountFromRow(puzzle);
        const existing = groups.get(characterCount) || [];
        existing.push(puzzle);
        groups.set(characterCount, existing);
    });

    return [...groups.entries()]
        .sort(([a], [b]) => a - b)
        .map(([characterCount, items]) => {
            const assignedLevels = items
                .filter(hasAssignedPuzzleLevel)
                .map((item) => getDisplayPuzzleLevel(item));
            const scores = items
                .map((item) => extractTotalScore(item.details))
                .filter((score) => score != null);

            return {
                characterCount,
                puzzleCount: items.length,
                withLevel: assignedLevels.length,
                levelMin: assignedLevels.length ? Math.min(...assignedLevels) : null,
                levelMax: assignedLevels.length ? Math.max(...assignedLevels) : null,
                levelAvg: assignedLevels.length ? roundStat(averageValues(assignedLevels)) : null,
                scoreMin: scores.length ? Math.min(...scores) : null,
                scoreMax: scores.length ? Math.max(...scores) : null,
                scoreAvg: scores.length ? roundStat(averageValues(scores)) : null,
                puzzleSharePercent: puzzles.length
                    ? roundStat((items.length / puzzles.length) * 100)
                    : 0,
            };
        });
}

export function buildSeasonPublishedLevelGroups(puzzles) {
    const groups = new Map();

    puzzles.forEach((puzzle) => {
        const level = hasAssignedPuzzleLevel(puzzle) ? getDisplayPuzzleLevel(puzzle) : null;
        const bucket = level != null ? Math.floor((level - 1) / 100) * 100 + 1 : "unassigned";
        const key = String(bucket);
        const existing = groups.get(key) || [];
        existing.push(puzzle);
        groups.set(key, existing);
    });

    return [...groups.entries()]
        .sort(([a], [b]) => {
            if (a === "unassigned") return 1;
            if (b === "unassigned") return -1;
            return Number(a) - Number(b);
        })
        .map(([bucket, items]) => {
            const levels = items
                .filter(hasAssignedPuzzleLevel)
                .map((item) => getDisplayPuzzleLevel(item));
            const scores = items
                .map((item) => extractTotalScore(item.details))
                .filter((score) => score != null);

            return {
                levelBucket: bucket === "unassigned" ? "Unassigned" : `${bucket}–${Math.min(Number(bucket) + 99, SEASON_MAX_LEVEL)}`,
                puzzleCount: items.length,
                levelMin: levels.length ? Math.min(...levels) : null,
                levelMax: levels.length ? Math.max(...levels) : null,
                levelAvg: levels.length ? roundStat(averageValues(levels)) : null,
                scoreMin: scores.length ? Math.min(...scores) : null,
                scoreMax: scores.length ? Math.max(...scores) : null,
                scoreAvg: scores.length ? roundStat(averageValues(scores)) : null,
            };
        });
}

export function canEditPuzzleLevel(row, seasonPublishedCount = null) {
    if (isLevelLocked(row)) {
        return false;
    }
    if (seasonPublishedCount != null) {
        const season = extractSeasonFromRow(row);
        if (season && isSeasonCatalogComplete(seasonPublishedCount)) {
            return false;
        }
    }
    return true;
}
